import { Waterbody, WaterbodyDetail } from '@omniafishing/core';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useUserWaterbodies } from '../../hooks/use_user_waterbodies';
import { apiV1 } from '../../lib/api';
import { OmniaUrls } from '../../lib/urls';
import { hasAccessToken } from '../../redux/auth';
import { PinLegend } from '../map/pin_legend';
import { OmniaButton } from '../omnia_button/omnia_button';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';
import { WaterbodyImg } from '../waterbody_img/waterbody_img';
import { WaterbodyNotificationsModal } from '../waterbody_notifications_modal/waterbody_notifications_modal';
import styles from './lake_banner.less';

export const LakeBanner = ({ waterbodies }: { waterbodies: Waterbody[] }) => {
  const isLoggedIn = useSelector(hasAccessToken);
  const hasWaterbody = waterbodies.length > 0;
  const [waterbodyFollowModalOpen, setWaterbodyFollowModalOpen] = useState(false);
  const [waterbodyDetail, setWaterbodyDetail] = useState<WaterbodyDetail>();
  const { isWaterbodyInUserWaterbodies } = useUserWaterbodies();
  const isFavorite = isWaterbodyInUserWaterbodies(waterbodyDetail);
  const showWaterbodyDetail = waterbodies.length === 1;
  const videoWaterbody = waterbodies[0];

  const fetchAndSetWaterbodyDetail = () => {
    apiV1.waterbodyFetch(waterbodies[0].url_slug).then((res) => {
      setWaterbodyDetail(res.data.data);
    });
  };

  useEffect(() => {
    if (showWaterbodyDetail) {
      fetchAndSetWaterbodyDetail();
    }
  }, [showWaterbodyDetail]);

  return (
    <>
      {hasWaterbody && showWaterbodyDetail && (
        <section className={styles.lakeDetail}>
          <div className={styles.lakeDetailContent}>
            <div className={styles.lakeDetailImg}>
              <Link to={OmniaUrls.waterbody(videoWaterbody)}>
                <WaterbodyImg waterbody={videoWaterbody} width={245} hires />
              </Link>
            </div>
            <div>
              <p className={styles.lakeDetailName}>
                <PinLegend size={28} type={'high_activity'} />{' '}
                <Link
                  to={OmniaUrls.waterbody(videoWaterbody)}
                  className={styles.lakeDetailNameLink}
                >
                  {videoWaterbody.primary_name}
                </Link>
                {videoWaterbody.locales.length > 0 && (
                  <>
                    (
                    {videoWaterbody.locales.map((locale, i) => (
                      <React.Fragment key={locale.state.abbr}>
                        {i > 0 && ', '}
                        <Link
                          to={OmniaUrls.state(locale.state)}
                          className={styles.state}
                          target="_blank"
                        >
                          {locale.state.abbr}
                        </Link>
                      </React.Fragment>
                    ))}
                    )
                  </>
                )}
              </p>

              <p className={styles.lakeDetailInfo}>
                <Link to={OmniaUrls.waterbody(videoWaterbody)}>
                  {videoWaterbody.fishing_reports_count}{' '}
                  {pluralize('Fishing report', videoWaterbody.fishing_reports_count)}
                </Link>
                <Link to={OmniaUrls.waterbody(videoWaterbody)}>
                  {videoWaterbody.favorites_count}{' '}
                  {pluralize('Follower', videoWaterbody.favorites_count)}
                </Link>
              </p>
              <div className={styles.lakeDetailsButtons}>
                <OmniaLinkButton size="lg" kind="primary" to={OmniaUrls.waterbody(videoWaterbody)}>
                  View Lake Details
                </OmniaLinkButton>
                <OmniaButton
                  size="lg"
                  kind="tertiary"
                  onPress={() => {
                    if (waterbodyDetail) {
                      if (isFavorite) {
                        apiV1.waterbodyUnfavorite(waterbodyDetail.url_slug).then((res) => {
                          setWaterbodyDetail(res.data.data);
                        });
                      } else {
                        setWaterbodyFollowModalOpen(true);
                      }
                    }
                  }}
                >
                  {isFavorite ? 'Unfollow' : 'Follow'}
                </OmniaButton>
                {waterbodyDetail && (
                  <WaterbodyNotificationsModal
                    waterbody={waterbodyDetail}
                    favorite={isFavorite}
                    event_notifications={waterbodyDetail.user_event_notifications}
                    open={waterbodyFollowModalOpen}
                    onClose={() => setWaterbodyFollowModalOpen(false)}
                    onSuccess={() => {
                      // refetch waterbody to get latest user_preferences and user_event_notifications
                      if (isLoggedIn) {
                        fetchAndSetWaterbodyDetail();
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};
